import { useEffect, useRef, useState } from 'react';
import { Button, Input, InputNumber, Select, Space, Typography } from 'antd';

import ToggleRow from '../common/ToggleRow';
import { CopyTextButton } from '../common/ShowAsText';
import { CLOSE, PLUS } from '../icons';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { checkPattern, matchesPattern, patternRefusalText } from '../../utils/textRules';
import {
	COUNT_TYPES, EMPTY_PIECES, KINDS, MAX_COUNT, MAX_ROWS, completeRowsRegex, describeRows, editRow, fitsPieces,
	kindOf, newRow, parseRegex, piecesProblem, proposeFromExamples, rowProblem, rowsToRegex,
} from '../../utils/patternPieces';

const EXAMPLE_SLOTS = 3;

/**
 * A short text's custom pattern, built without writing a regular expression
 * (KPR-956, KPE-010; the owner's rulings p01 and p02, 2026-10-18;
 * docs/platform-standards.md §15). Two views over the one stored
 * `{ regex, flags?, message }`:
 *
 *   Pieces       rows — Starts with / then / Ends with, each a kind (letters,
 *                digits, the text …, any of these characters …) and a count
 *                (exactly, from … to …, one or more, any number). keepr writes
 *                the regex from them (utils/patternPieces.js). Under the rows,
 *                Examples: values that should fit propose the rows (p01), and
 *                each is marked Matches or Doesn't match against them.
 *   Expression   behind "Edit as expression": the regex as text, with Copy,
 *                and Back to pieces when the rows can show it.
 *
 * p02: a stored regex the rows express exactly — they write it back byte for
 * byte — reopens as rows; any other opens as the expression and is never
 * rewritten unless the person edits it. Rows that cannot be written yet (one
 * not filled in, too slow to check) write an empty regex, which the API
 * refuses on save, so they never store a pattern its probe would refuse.
 * "Letters in either case" (the `i` flag) is shown in both views.
 *
 *   pattern       the stored custom pattern ({ regex, flags?, message })
 *   onChange      (nextPattern) => void
 *   serverReason  the API's refusal reason for this pattern, if any
 *   normalize     a value as it will be stored (the element's trim and case)
 */
export default function PatternEditor({ idPrefix, pattern, onChange, serverReason = null, normalize = v => v, size = 'large', compact = false }) {
	const regex = typeof pattern?.regex === 'string' ? pattern.regex : '';
	const flags = pattern?.flags === 'i' ? 'i' : '';
	const idFor = key => `${idPrefix}.${key}`;
	const small = size === 'small';

	const [mode, setMode] = useState(() => (!regex || fitsPieces(regex, flags) ? 'pieces' : 'expression'));
	const [pieces, setPieces] = useState(() => (regex && fitsPieces(regex, flags) ? parseRegex(regex) : EMPTY_PIECES));
	const [examples, setExamples] = useState(() => Array(EXAMPLE_SLOTS).fill(''));
	const [note, setNote] = useState(null);
	// The regex this editor last wrote: a stored one that differs came from
	// outside (the card reloaded) and is read again.
	const written = useRef(regex);

	useEffect(() => {
		if (regex === written.current) return;
		written.current = regex;
		setNote(null);
		if (!regex) { setPieces(EMPTY_PIECES); setMode('pieces'); return; }
		if (fitsPieces(regex, flags)) { setPieces(parseRegex(regex)); setMode('pieces'); }
		else setMode('expression');
	}, [regex, flags]);

	const write = next => {
		written.current = typeof next.regex === 'string' ? next.regex : '';
		onChange(next);
	};

	const problem = mode === 'pieces' ? piecesProblem(pieces, flags) : null;

	const commit = (nextPieces, nextFlags = flags) => {
		setPieces(nextPieces);
		const blocked = piecesProblem(nextPieces, nextFlags);
		const next = { ...pattern, regex: blocked ? '' : rowsToRegex(nextPieces) };
		if (nextFlags !== flags) next.flags = nextFlags || undefined;
		write(next);
	};

	const rows = pieces.rows;
	const changeRow = (index, change) => {
		setNote(null);
		commit({ ...pieces, rows: rows.map((row, i) => (i === index ? editRow(row, change) : row)) });
	};
	const addRow = () => { setNote(null); commit({ ...pieces, rows: [...rows, newRow()] }); };
	const removeRow = index => { setNote(null); commit({ ...pieces, rows: rows.filter((_, i) => i !== index) }); };

	const changeKind = (index, kind) => {
		const row = rows[index];
		const keepText = kindOf(kind)?.needsText && kindOf(row.kind)?.needsText;
		changeRow(index, { kind, text: keepText ? row.text : '' });
	};
	const changeCountType = (index, type) => {
		const count = rows[index].count || {};
		const n = Number.isInteger(count.n) ? count.n : 1;
		if (type === 'exactly') changeRow(index, { count: { type, n: Math.max(1, n), m: null } });
		else if (type === 'range') changeRow(index, { count: { type, n, m: Math.max(n, Number.isInteger(count.m) ? count.m : n + 1, 1) } });
		else changeRow(index, { count: { type, n: null, m: null } });
	};
	const changeCount = (index, key, value) => {
		changeRow(index, { count: { ...rows[index].count, [key]: Number.isInteger(value) ? value : null } });
	};

	const propose = () => {
		const proposal = proposeFromExamples(examples.map(v => (v ? normalize(v) : '')));
		if (!proposal) return;
		commit({ rows: proposal.rows, lead: '', trail: '' });
		setNote(proposal.note);
	};

	const toggleCase = on => {
		const nextFlags = on ? 'i' : '';
		if (mode === 'pieces') commit(pieces, nextFlags);
		else write({ ...pattern, flags: on ? 'i' : undefined });
	};

	const openExpression = () => {
		// Rows that cannot be written yet: show what the complete ones write.
		if (problem && rows.length) write({ ...pattern, regex: completeRowsRegex(pieces) });
		setNote(null);
		setMode('expression');
	};
	const fitsNow = !regex || fitsPieces(regex, flags);
	const backToPieces = () => {
		setPieces(regex ? parseRegex(regex) : EMPTY_PIECES);
		setMode('pieces');
	};

	// What a value is checked against, when there is one to check.
	const testable = regex ? checkPattern({ regex, flags: flags || undefined, message: 'm' }).ok : false;
	const marks = examples.map(value => (value && testable ? matchesPattern(normalize(value), { regex, flags }) : null));
	const hasExamples = examples.some(Boolean);

	// The refusal said in this view's words.
	const local = mode === 'expression' && regex ? checkPattern({ regex, flags: flags || undefined, message: 'm' }) : null;
	const expressionReason = (local && !local.ok ? local.reason : null) || (mode === 'expression' ? serverReason : null);
	const piecesAlert = mode === 'pieces'
		? (problem && problem.reason !== 'empty' && problem.reason !== 'row' ? problem.text : null)
			|| (serverReason && !problem ? patternRefusalText(serverReason) : null)
			|| (serverReason && problem?.reason === 'empty' ? problem.text : null)
		: null;
	const sentence = mode === 'pieces' && !problem ? describeRows(rows) : null;

	const caseToggle = (
		<ToggleRow
			first
			label='Letters in either case'
			description='Capital and small letters count as the same: “abc” fits where “ABC” does.'
			checked={flags === 'i'}
			onChange={toggleCase}
			size={compact ? 'small' : undefined}
		/>
	);

	const examplesPanel = (
		<div className='pattern-editor__examples mb-2'>
			<div className='d-block' id={idFor('examples-label')}>Examples</div>
			<div className='form-panel__hint'>
				{mode === 'pieces'
					? `Type two or three values that should fit, then propose rows from them${rows.length ? ' (they replace the rows above)' : ''}.`
					: 'Type values that should fit, to check the expression against them.'}
			</div>
			<div className='pattern-editor__example-list' role='group' aria-labelledby={idFor('examples-label')}>
				{examples.map((value, index) => (
					<div className='pattern-editor__example' key={index}>
						<Input
							{...NO_TEXT_ASSIST}
							id={idFor(`example-${index}`)}
							size={size}
							value={value}
							maxLength={MAX_COUNT}
							onChange={e => setExamples(examples.map((v, i) => (i === index ? e.target.value : v)))}
							placeholder={index === 0 ? 'A value that should fit' : 'Another'}
							status={marks[index] === false ? 'error' : undefined}
							aria-label={`Example ${index + 1}`}
							aria-describedby={marks[index] === null ? undefined : idFor(`example-${index}-mark`)}
						/>
						{marks[index] === null ? null : (
							<Typography.Text
								id={idFor(`example-${index}-mark`)}
								className='pattern-editor__mark'
								type={marks[index] ? 'secondary' : 'danger'}
								data-testid={`pattern-example-${index}-mark`}
							>
								{marks[index] ? 'Matches' : 'Doesn’t match'}
							</Typography.Text>
						)}
					</div>
				))}
			</div>
			{mode === 'pieces' ? (
				<Button size={small ? 'small' : undefined} className='pattern-editor__propose' disabled={!hasExamples} onClick={propose}>
					Propose rows
				</Button>
			) : null}
			{note ? <div className='form-panel__hint' data-testid='pattern-proposal-note'>{note}</div> : null}
		</div>
	);

	if (mode === 'expression') {
		return (
			<div className='pattern-editor pattern-editor--expression'>
				<div className='mb-2'>
					<label className='d-block' htmlFor={idFor('regex')}>Expression</label>
					<Input
						{...NO_TEXT_ASSIST}
						id={idFor('regex')}
						size={size}
						className='text-options__regex'
						value={regex}
						onChange={e => write({ ...pattern, regex: e.target.value })}
						status={expressionReason ? 'error' : undefined}
						aria-label='Expression'
					/>
					{expressionReason ? <div className='form-panel__hint' role='alert'>{patternRefusalText(expressionReason)}</div> : null}
					<div className='form-panel__hint'>
						The pattern as a regular expression. The whole value must match. Letters, digits and symbols stand for themselves; [A-Z] is one capital letter, {'{3}'} three of the thing before it, ? makes it optional, and | separates alternatives.
					</div>
					<Space wrap className='pattern-editor__actions'>
						{fitsNow ? <Button size={small ? 'small' : undefined} onClick={backToPieces}>Back to pieces</Button> : null}
						<CopyTextButton text={regex} size={small ? 'small' : undefined} />
					</Space>
					{!fitsNow && !expressionReason ? (
						<div className='form-panel__hint'>The pieces can’t show this expression, so it stays as an expression.</div>
					) : null}
				</div>
				{caseToggle}
				{examplesPanel}
			</div>
		);
	}

	return (
		<div className='pattern-editor pattern-editor--pieces'>
			<div className='mb-2'>
				<div className='d-block' id={idFor('rows-label')}>Pieces</div>
				<div className='pattern-editor__rows' role='group' aria-labelledby={idFor('rows-label')}>
					{rows.map((row, index) => {
						const kind = kindOf(row.kind);
						const count = row.count || {};
						const label = `Row ${index + 1}`;
						const join = index === 0 ? (rows.length === 1 ? 'The whole value is' : 'Starts with') : (index === rows.length - 1 ? 'Ends with' : 'then');
						const issue = rowProblem(row);
						const countIssue = Boolean(issue && /count/i.test(issue));
						return (
							<div className='pattern-editor__row' key={row.id} role='group' aria-label={label}>
								<span className='pattern-editor__join'>{join}</span>
								<Select
									id={idFor(`${row.id}.kind`)}
									size={size}
									className='pattern-editor__kind'
									value={row.kind}
									onChange={value => changeKind(index, value)}
									options={KINDS.map(k => ({ value: k.id, label: k.label }))}
									aria-label={`${label}: what it takes`}
									virtual={false}
								/>
								{kind?.needsText ? (
									<Input
										{...NO_TEXT_ASSIST}
										size={size}
										className='pattern-editor__text'
										value={row.text}
										onChange={e => changeRow(index, { text: e.target.value })}
										placeholder={row.kind === 'text' ? 'Text, like INV' : 'Characters, like - . or _'}
										status={issue && !row.text ? 'error' : undefined}
										aria-label={`${label}: ${row.kind === 'text' ? 'text' : 'characters'}`}
									/>
								) : null}
								<Select
									id={idFor(`${row.id}.count`)}
									size={size}
									className='pattern-editor__count'
									value={count.type}
									onChange={value => changeCountType(index, value)}
									options={COUNT_TYPES}
									aria-label={`${label}: how many`}
									virtual={false}
								/>
								{count.type === 'exactly' || count.type === 'range' ? (
									<span className='pattern-editor__numbers'>
										<InputNumber
											size={size}
											className='pattern-editor__number'
											min={count.type === 'range' ? 0 : 1}
											max={MAX_COUNT}
											step={1}
											precision={0}
											inputMode='numeric'
											value={count.n}
											onChange={value => changeCount(index, 'n', value)}
											status={countIssue ? 'error' : undefined}
											aria-label={`${label}: ${count.type === 'range' ? 'at least' : 'count'}`}
										/>
										{count.type === 'range' ? (
											<>
												<span className='pattern-editor__word'>to</span>
												<InputNumber
													size={size}
													className='pattern-editor__number'
													min={1}
													max={MAX_COUNT}
													step={1}
													precision={0}
													inputMode='numeric'
													value={count.m}
													onChange={value => changeCount(index, 'm', value)}
													status={countIssue ? 'error' : undefined}
													aria-label={`${label}: at most`}
												/>
											</>
										) : null}
									</span>
								) : null}
								<Button
									size={size}
									type='text'
									className='pattern-editor__remove'
									icon={<CLOSE />}
									onClick={() => removeRow(index)}
									aria-label={`Remove row ${index + 1}`}
								/>
								{issue ? <Typography.Text type='danger' className='pattern-editor__row-issue'>{issue}</Typography.Text> : null}
							</div>
						);
					})}
				</div>
				{problem?.reason === 'empty' && !serverReason ? <div className='form-panel__hint'>{problem.text}</div> : null}
				{piecesAlert ? <div className='form-panel__hint' role='alert'>{piecesAlert}</div> : null}
				{sentence ? <div className='form-panel__hint' data-testid='pattern-sentence'>A value fits when it is {sentence}.</div> : null}
				<Space wrap className='pattern-editor__actions'>
					<Button size={small ? 'small' : undefined} icon={<PLUS />} disabled={rows.length >= MAX_ROWS} onClick={addRow}>Add a row</Button>
					<Button type='link' size='small' onClick={openExpression}>Edit as expression</Button>
				</Space>
			</div>
			{caseToggle}
			{examplesPanel}
		</div>
	);
}
